/*
 * Motion for the cart drawer's free-shipping progress panel.
 *
 * Why this needs JS at all: Dawn replaces the whole drawer's innerHTML on every
 * cart update, so the progress fill is always a brand-new element that already
 * carries its final width - a CSS transition has nothing to animate from.
 *
 * How it moves (v2, 2026-09-22):
 *  - The bar's position lives here, not in the DOM, and follows a critically
 *    damped spring: it eases in, glides, and settles without overshooting.
 *  - When the drawer re-renders mid-movement, or the customer changes the
 *    quantity again before the bar has settled, the bar carries on from where
 *    it is on screen, keeping its speed - no jumps back and no restarts.
 *  - The fill is moved with transform (GPU) instead of width, so it stays
 *    smooth while the rest of the drawer is re-laying out.
 *  - The sheen keeps one steady rhythm across re-renders instead of
 *    restarting every time the cart changes.
 *
 * Loaded from snippets/cart-drawer.liquid OUTSIDE .drawer__inner, because
 * scripts injected via innerHTML never execute.
 *
 * Purely decorative: if this file fails to load, the panel still renders at the
 * correct width from its inline style. Nothing here is load-bearing.
 *
 * Previous version: assets/backup-20260922f-eh-cart-shipping-motion-js.txt
 */
(function () {
  if (window.ehShippingMotionBound) return;
  window.ehShippingMotionBound = true;

  var CHANGED = 'is-eh-changed';
  var UNLOCKED = 'is-eh-unlocked';
  var INTRO_DELAY = 180; // ms - let the drawer finish sliding in first
  var OMEGA = 6.5; // spring speed: ~0.6s to cover 90% of the way, ~1s to settle
  var SHEEN_PERIOD = 2800; // ms - must match the sheen animation in the CSS

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Spring segment: at time t0 the bar was at x0 (%) moving at v0 (%/s),
  // heading for target (%). A new segment starts whenever the target changes.
  var spring = null;
  var fill = null;
  var lastTarget = null;
  var lastUnlocked = null;
  var playedOpen = false;
  var dirty = true;
  var raf = null;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function drawerIsOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  // Closed-form critically damped spring, so the position is exact at any
  // moment regardless of frame rate or skipped frames.
  function sample(t) {
    if (t <= spring.t0) return { x: spring.x0, v: spring.v0 };
    var dt = (t - spring.t0) / 1000;
    var d0 = spring.x0 - spring.target;
    var a = spring.v0 + OMEGA * d0;
    var e = Math.exp(-OMEGA * dt);
    return {
      x: spring.target + (d0 + a * dt) * e,
      v: (spring.v0 - OMEGA * a * dt) * e
    };
  }

  function startSpring(t, target) {
    if (spring && t < spring.t0) {
      // Still waiting for the intro delay: just aim somewhere else.
      spring.target = target;
      return;
    }
    var now = spring ? sample(t) : { x: target, v: 0 };
    spring = { x0: now.x, v0: now.v, t0: t, target: target };
  }

  function render(x) {
    fill.style.transform = 'translateX(' + (clamp(x, 0, 100) - 100).toFixed(3) + '%)';
  }

  // A freshly rendered fill: make it full width and slide it with transform
  // from here on. Runs before the browser paints, so it never flashes.
  function adopt(el, t) {
    fill = el;
    fill.style.width = '100%';
    fill.style.setProperty('--eh-sheen-delay', -(t % SHEEN_PERIOD).toFixed(0) + 'ms');
  }

  function read(t) {
    var panel = document.querySelector('[data-eh-shipping]');
    if (!panel) {
      fill = null;
      return;
    }

    var target = parseFloat(panel.getAttribute('data-progress'));
    target = isNaN(target) ? 0 : clamp(target, 0, 100);

    var unlocked = panel.getAttribute('data-unlocked') === 'true';
    var open = drawerIsOpen();

    var changed = lastTarget !== null && lastTarget !== target;
    var justUnlocked = unlocked && lastUnlocked === false;
    var intro = open && !playedOpen;

    if (open) playedOpen = true;
    lastTarget = target;
    lastUnlocked = unlocked;

    // Reduced motion: leave the server-rendered width alone.
    if (reduce) return;

    if (changed) panel.classList.add(CHANGED);
    if (justUnlocked) panel.classList.add(UNLOCKED);

    var el = panel.querySelector('.eh-cart-shipping__track > span');
    if (!el) {
      fill = null;
      return;
    }
    if (el !== fill) adopt(el, t);

    if (intro) {
      // First time the drawer opens: grow from empty once it has slid in.
      spring = { x0: 0, v0: 0, t0: t + INTRO_DELAY, target: target };
    } else if (!spring) {
      // Page load with the drawer closed: just sit at the right value.
      spring = { x0: target, v0: 0, t0: t, target: target };
    } else if (spring.target !== target) {
      startSpring(t, target);
    }
  }

  function tick(t) {
    raf = null;

    if (dirty) {
      dirty = false;
      read(t);
    }

    if (!fill || !spring) return;
    if (!fill.isConnected) {
      fill = null;
      return;
    }

    var now = sample(t);
    var settled =
      t >= spring.t0 &&
      Math.abs(now.x - spring.target) < 0.05 &&
      Math.abs(now.v) < 0.5;

    if (settled) {
      spring = { x0: spring.target, v0: 0, t0: t, target: spring.target };
      render(spring.target);
      return;
    }

    render(now.x);
    raf = requestAnimationFrame(tick);
  }

  // Collapse the burst of mutations a re-render produces into one pass, and
  // share a single animation loop with the movement itself.
  function schedule() {
    dirty = true;
    if (!raf) raf = requestAnimationFrame(tick);
  }

  function start() {
    schedule();

    var host = document.getElementById('CartDrawer');
    if (host) {
      new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
    }

    // The drawer sliding open is its own cue, separate from the cart changing.
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(schedule).observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
